import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { KeyRound, Plus, Trash2, Copy, Check, ShieldAlert, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { ApiDocs } from "./ApiDocs";
import { RetellSignatureCard } from "./RetellSignatureCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "Never");

const NewKeyReveal = ({ apiKey, onDismiss }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(apiKey.api_key); setCopied(true); toast.success("API key copied"); } catch { toast.error("Copy failed — select and copy manually"); }
  };
  return (
    <div className="p-4 bg-emerald-50 border border-emerald-300 rounded-lg space-y-3" data-testid="api-key-reveal">
      <div className="flex items-start gap-2">
        <ShieldAlert className="w-5 h-5 text-emerald-700 mt-0.5" />
        <div>
          <p className="font-semibold text-emerald-900">Key "{apiKey.name}" created — copy it now</p>
          <p className="text-sm text-emerald-800">This is the only time the full key is shown. Store it in Retell (or your integration) as the <code>X-API-Key</code> header.</p>
        </div>
      </div>
      <div className="flex gap-2">
        <Input readOnly value={apiKey.api_key} className="font-mono text-sm bg-white" onFocus={(e) => e.target.select()} data-testid="api-key-reveal-value" />
        <Button type="button" onClick={copy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="api-key-reveal-copy">{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}</Button>
      </div>
      <Button type="button" variant="outline" size="sm" onClick={onDismiss} data-testid="api-key-reveal-dismiss">I've saved it</Button>
    </div>
  );
};

export const ApiAccessSettings = () => {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null);

  const load = async () => {
    try { const { data } = await axios.get(`${API}/api-keys`); setKeys(data); } catch { toast.error("Could not load API keys"); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/api-keys`, { name: name.trim() });
      setNewKey(data); setName("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create key");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Any integration using it will stop working immediately.`)) return;
    try { await axios.delete(`${API}/api-keys/${k.id}`); toast.success(`Revoked "${k.name}"`); await load(); } catch (err) { toast.error(err.response?.data?.detail || "Could not revoke"); }
  };

  const active = keys.filter((k) => k.active);
  const revoked = keys.filter((k) => !k.active);

  return (
    <div className="space-y-6" data-testid="api-access-settings">
      <Card className="bg-white border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-indigo-600" /> API Keys</CardTitle>
          <p className="text-sm text-slate-600">Keys give external systems (like your Retell AI phone agent) full admin access to the API: customers, appointments, invoices, statements, quotes, payments and reports.</p>
        </CardHeader>
        <CardContent className="space-y-5">
          <form onSubmit={create} className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[240px]">
              <Label htmlFor="api-key-name">Key name</Label>
              <Input id="api-key-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Retell AI" maxLength={80} data-testid="api-key-name-input" />
            </div>
            <Button type="submit" disabled={creating || !name.trim()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="api-key-create-button"><Plus className="w-4 h-4 mr-1" /> {creating ? "Generating…" : "Generate key"}</Button>
          </form>

          {newKey && <NewKeyReveal apiKey={newKey} onDismiss={() => setNewKey(null)} />}

          <div className="border border-slate-200 rounded-lg overflow-x-auto">
            <Table data-testid="api-keys-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Name</TableHead><TableHead>Key</TableHead><TableHead>Created</TableHead><TableHead>Last used</TableHead><TableHead className="text-right">Calls</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {active.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-slate-500" data-testid="api-keys-empty">No active API keys yet. Generate one to connect Retell AI.</TableCell></TableRow>}
                {active.map((k) => (
                  <TableRow key={k.id} data-testid="api-key-row">
                    <TableCell className="font-medium text-slate-800">{k.name}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-600">{k.prefix}…</TableCell>
                    <TableCell className="text-sm text-slate-600 whitespace-nowrap">{fmt(k.created_at)}<p className="text-xs text-slate-400">by {k.created_by}</p></TableCell>
                    <TableCell className="text-sm text-slate-600 whitespace-nowrap" data-testid="api-key-last-used">{fmt(k.last_used_at)}</TableCell>
                    <TableCell className="text-right text-sm text-slate-600">{k.use_count || 0}</TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => revoke(k)} data-testid="api-key-revoke-button"><Trash2 className="w-4 h-4 mr-1" /> Revoke</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {revoked.length > 0 && (
            <details className="text-sm text-slate-500" data-testid="api-keys-revoked">
              <summary className="cursor-pointer">Revoked keys ({revoked.length})</summary>
              <ul className="mt-2 space-y-1">{revoked.map((k) => <li key={k.id} className="flex items-center gap-2"><Badge variant="outline" className="text-slate-500">revoked</Badge>{k.name} <span className="font-mono text-xs">{k.prefix}…</span> <span className="text-xs">on {fmt(k.revoked_at)}</span></li>)}</ul>
            </details>
          )}
        </CardContent>
      </Card>

      <RetellSignatureCard />

      <Card className="bg-white border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-indigo-600" /> API Documentation &amp; Retell AI setup</CardTitle>
        </CardHeader>
        <CardContent><ApiDocs /></CardContent>
      </Card>
    </div>
  );
};
